import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Chip, Avatar, Divider, CircularProgress, Alert, Grid, Paper,
} from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import SEOHead from '../components/SEO/SEOHead';
import CommentSection from '../components/Blog/CommentSection';
import ArticleCard from '../components/Blog/ArticleCard';
import BlogService from '../services/blog-service';
import { BlogArticle } from '../types/blog';

const BlogArticlePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<BlogArticle | null>(null);
  const [related, setRelated] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await BlogService.getArticleBySlug(slug);
        setArticle(data);
        // Fetch related articles by same category
        try {
          const res = await BlogService.listArticles({ category: data.category, limit: 4, status: 'published' });
          setRelated((res.articles || []).filter((a) => a.articleId !== data.articleId).slice(0, 3));
        } catch { /* ignore */ }
      } catch {
        setError('Article not found.');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [slug]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error || !article) return (
    <Paper sx={{ p: 4, textAlign: 'center', my: 4 }}>
      <Typography variant="h5" color="error" gutterBottom>{error || 'Article not found'}</Typography>
      <Typography variant="body1">
        <RouterLink to="/blog">Back to Blog</RouterLink>
      </Typography>
    </Paper>
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    author: { '@type': 'Person', name: article.authorName },
    datePublished: article.createdAt,
    dateModified: article.updatedAt,
    image: article.featuredImageUrl,
    description: article.metaDescription,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://hooliah.com/blog/${article.slug}` },
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <SEOHead
        title={article.metaTitle || article.title}
        description={article.metaDescription}
        canonicalUrl={`https://hooliah.com/blog/${article.slug}`}
        ogType="article"
        ogImage={article.featuredImageUrl}
        jsonLd={jsonLd}
      />

      {/* Category */}
      <Chip label={article.category} color="primary" variant="outlined" sx={{ mb: 2 }} />

      {/* Title */}
      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 2 }}>{article.title}</Typography>

      {/* Author + meta */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar src={article.authorAvatar} alt={article.authorName} sx={{ width: 40, height: 40 }}>
          {article.authorName?.[0]}
        </Avatar>
        <Box>
          <Typography variant="subtitle2">{article.authorName}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {new Date(article.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </Typography>
            <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">{article.readingTimeMinutes} min read</Typography>
          </Box>
        </Box>
      </Box>

      {/* Featured image */}
      {article.featuredImageUrl && (
        <Box component="img" src={article.featuredImageUrl} alt={article.title}
          sx={{ width: '100%', borderRadius: 2, mb: 3, maxHeight: 480, objectFit: 'cover' }}
        />
      )}

      {/* Article body — no ad blocks within article content */}
      <Box sx={{ '& img': { maxWidth: '100%', borderRadius: 1 }, typography: 'body1', lineHeight: 1.8 }}
        dangerouslySetInnerHTML={{ __html: article.body }}
      />

      {/* Tags */}
      {article.tags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
          {article.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" component={RouterLink} to={`/blog?tag=${tag}`} clickable />
          ))}
        </Box>
      )}

      <Divider sx={{ my: 4 }} />

      {/* Comments */}
      <CommentSection articleId={article.slug} />

      {/* Related articles */}
      {related.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>Related Articles</Typography>
          <Grid container spacing={3}>
            {related.map((a) => (
              <Grid item xs={12} sm={4} key={a.articleId}>
                <ArticleCard article={a} />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default BlogArticlePage;
